import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getPortalOrderDetail,
  getPortalOrderFormData,
  updatePortalOrderFull,
  updatePortalOrderShipTo,
  deletePortalOrder,
  holdPortalOrder,
  cancelPortalOrder,
} from "@/lib/portal.functions";
import { listCustomFields } from "@/lib/custom-fields.functions";
import { CustomFieldsForm, type CustomFieldDef } from "@/components/custom-fields/dynamic-fields";
import { validateCustomFields } from "@/lib/orders/custom-field-validate";
import {
  createRmaFromOrder,
  shopReturnRates,
  purchaseReturnLabel,
} from "@/lib/return-labels.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { OrderSummaryHeader } from "@/components/orders/order-summary-header";
import { getOrderOperationalSummary } from "@/lib/orders/summary.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Loader2, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/orders/$orderId")({
  component: PortalOrderDetail,
});

type ShipTo = {
  name: string; company: string; street1: string; street2: string;
  city: string; state: string; postal_code: string; country: string;
  phone: string; email: string;
};

const EMPTY_SHIP_TO: ShipTo = {
  name: "", company: "", street1: "", street2: "",
  city: "", state: "", postal_code: "", country: "US",
  phone: "", email: "",
};

const PRE_PACKED = new Set([
  "draft", "open", "pending_allocation", "allocated", "backordered",
  "picking", "picked", "on_hold", "exception",
]);
const FULL_EDIT = new Set(["draft", "open"]);

function PortalOrderDetail() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const fetchDetail = useServerFn(getPortalOrderDetail);
  const fetchSummary = useServerFn(getOrderOperationalSummary);
  const fetchForm = useServerFn(getPortalOrderFormData);
  const updateFull = useServerFn(updatePortalOrderFull);
  const updateShipFn = useServerFn(updatePortalOrderShipTo);
  const deleteFn = useServerFn(deletePortalOrder);
  const holdFn = useServerFn(holdPortalOrder);
  const cancelFn = useServerFn(cancelPortalOrder);
  const fetchCustomFields = useServerFn(listCustomFields);

  const { data, isLoading } = useQuery({
    queryKey: ["portal-order", orderId],
    queryFn: () => runServerFn(() => fetchDetail({ data: { orderId } }), "Loading order"),
  });
  const { data: summary } = useQuery({
    queryKey: ["portal-order-summary", orderId],
    queryFn: () => runServerFn(() => fetchSummary({ data: { orderId } }), "Loading summary"),
  });
  const { data: form } = useQuery({
    queryKey: ["portal-order-form"],
    queryFn: () => runServerFn(() => fetchForm(), "Loading form"),
  });
  const tenantId = data?.order?.tenant_id;
  const { data: cfData } = useQuery({
    queryKey: ["portal-order-custom-fields", tenantId],
    enabled: !!tenantId,
    queryFn: () => runServerFn(
      () => fetchCustomFields({ data: { tenantId: tenantId!, entity: "orders", enabledOnly: true } }),
      "Loading custom fields",
    ),
  });
  const cfDefs = (cfData?.rows ?? []) as CustomFieldDef[];

  const order = data?.order;
  const status = order?.status ?? "";
  const canFullEdit = FULL_EDIT.has(status);
  const canEditShip = PRE_PACKED.has(status);
  const canHold = canEditShip && status !== "on_hold";
  const canRelease = status === "on_hold";
  const canDelete = canFullEdit;
  const canCancel = PRE_PACKED.has(status) && status !== "cancelled";

  const [rmaOpen, setRmaOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  if (isLoading || !order) {
    return <div className="page-container py-12 text-center text-muted-foreground">Loading…</div>;
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["portal-order", orderId] });
    qc.invalidateQueries({ queryKey: ["portal-orders"] });
  };

  const onHold = async (hold: boolean) => {
    try {
      await runServerFn(() => holdFn({ data: { orderId, hold } }), hold ? "Holding" : "Releasing");
      toast.success(hold ? "Order on hold" : "Hold released");
      refresh();
    } catch (e) { toast.error((e as Error).message); }
  };

  const onDelete = async () => {
    try {
      await runServerFn(() => deleteFn({ data: { orderId } }), "Deleting");
      toast.success("Order deleted");
      navigate({ to: "/portal/orders" });
    } catch (e) { toast.error((e as Error).message); }
  };

  const onCancel = async () => {
    try {
      await runServerFn(() => cancelFn({ data: { orderId, reason: null } }), "Cancelling");
      toast.success("Order cancelled");
      refresh();
    } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <>
      <PortalPageHeader
        title={`Order ${order.order_number}`}
        description={
          summary ? undefined : `Status: ${status} · Created ${new Date(order.created_at).toLocaleString()}`
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" asChild>
              <Link to="/portal/orders"><ArrowLeft className="h-4 w-4 mr-1" />All orders</Link>
            </Button>
            {canHold && (
              <Button size="sm" variant="outline" onClick={() => onHold(true)}>
                Put on hold
              </Button>
            )}
            {canRelease && (
              <Button size="sm" variant="outline" onClick={() => onHold(false)}>
                Release hold
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setRmaOpen(true)}>
              Create RMA
            </Button>
            {canCancel && (
              <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)}>
                Cancel order
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="destructive" onClick={() => setDelOpen(true)}>
                Delete
              </Button>
            )}
          </div>
        }
      />
      <div className="page-container space-y-6">
        {summary && (
          <OrderSummaryHeader summary={summary} variant="portal" />
        )}
        {!canFullEdit && canEditShip && (
          <p className="text-xs text-muted-foreground">
            Order is <strong>{status}</strong> — only the shipping address can be changed.
          </p>
        )}
        {!canEditShip && (
          <p className="text-xs text-muted-foreground">
            Order is <strong>{status}</strong> — locked for edits. You can still create an RMA below.
          </p>
        )}

        {canFullEdit ? (
          <FullEditForm
            order={order}
            initialLines={data!.lines}
            customFieldDefs={cfDefs}
            skus={(form?.skus ?? []).concat(
              // Ensure existing SKUs render even if not in default form list
              (data!.skus ?? []).filter((s) => !(form?.skus ?? []).some((fs) => fs.id === s.id)),
            )}
            onSave={async (payload) => {
              await runServerFn(() => updateFull({ data: { orderId, ...payload } as never }), "Saving");
              toast.success("Order updated");
              refresh();
            }}
          />
        ) : (
          <ShipToOnlyForm
            disabled={!canEditShip}
            initial={normalizeShipTo(order.ship_to)}
            onSave={async (shipTo) => {
              await runServerFn(() => updateShipFn({ data: { orderId, shipTo } }), "Saving");
              toast.success("Shipping address updated");
              refresh();
            }}
          />
        )}

        <ReadOnlySummary
          order={order}
          lines={data!.lines}
          skus={data!.skus}
          hideShipTo={canEditShip}
          hideLines={canFullEdit}
        />
      </div>

      <RmaDialog
        open={rmaOpen}
        onOpenChange={setRmaOpen}
        orderId={orderId}
        orderNumber={order.order_number}
        lines={data!.lines}
        skus={data!.skus}
        onDone={() => { setRmaOpen(false); refresh(); }}
      />

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order {order.order_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order and its lines. Allowed only while the order is draft or open.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel order {order.order_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The order will move to status <strong>cancelled</strong> and cannot be re-opened.
              Allowed only before the order is packed or shipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep order</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel}>Cancel order</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function normalizeShipTo(raw: unknown): ShipTo {
  const s = (raw ?? {}) as Record<string, string | null | undefined>;
  return {
    name: s.name ?? "", company: s.company ?? "",
    street1: s.street1 ?? "", street2: s.street2 ?? "",
    city: s.city ?? "", state: s.state ?? "",
    postal_code: s.postal_code ?? "", country: s.country ?? "US",
    phone: s.phone ?? "", email: s.email ?? "",
  };
}

function cleanShipTo(s: ShipTo) {
  return {
    name: s.name.trim(),
    company: s.company.trim() || null,
    street1: s.street1.trim(),
    street2: s.street2.trim() || null,
    city: s.city.trim(),
    state: s.state.trim(),
    postal_code: s.postal_code.trim(),
    country: s.country.trim().toUpperCase(),
    phone: s.phone.trim() || null,
    email: s.email.trim() || null,
  };
}

function ShipToFields({
  value, onChange, disabled,
}: { value: ShipTo; onChange: (v: ShipTo) => void; disabled?: boolean }) {
  const set = (patch: Partial<ShipTo>) => onChange({ ...value, ...patch });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Input disabled={disabled} required placeholder="Name" value={value.name} onChange={(e) => set({ name: e.target.value })} />
      <Input disabled={disabled} placeholder="Company" value={value.company} onChange={(e) => set({ company: e.target.value })} />
      <Input disabled={disabled} required placeholder="Street 1" value={value.street1} onChange={(e) => set({ street1: e.target.value })} />
      <Input disabled={disabled} placeholder="Street 2" value={value.street2} onChange={(e) => set({ street2: e.target.value })} />
      <Input disabled={disabled} required placeholder="City" value={value.city} onChange={(e) => set({ city: e.target.value })} />
      <Input disabled={disabled} required placeholder="State" value={value.state} onChange={(e) => set({ state: e.target.value })} />
      <Input disabled={disabled} required placeholder="Postal code" value={value.postal_code} onChange={(e) => set({ postal_code: e.target.value })} />
      <Input disabled={disabled} required maxLength={2} placeholder="Country (ISO-2)" value={value.country} onChange={(e) => set({ country: e.target.value.toUpperCase() })} />
      <Input disabled={disabled} placeholder="Phone" value={value.phone} onChange={(e) => set({ phone: e.target.value })} />
      <Input disabled={disabled} type="email" placeholder="Email" value={value.email} onChange={(e) => set({ email: e.target.value })} />
    </div>
  );
}

function ShipToOnlyForm({
  initial, onSave, disabled,
}: { initial: ShipTo; onSave: (s: ReturnType<typeof cleanShipTo>) => Promise<void>; disabled: boolean }) {
  const [shipTo, setShipTo] = useState<ShipTo>(initial);
  const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(cleanShipTo(shipTo)); }
    catch (err) { toast.error((err as Error).message); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-4 space-y-3">
      <h3 className="font-medium">Ship To</h3>
      <form onSubmit={submit} className="space-y-3">
        <ShipToFields value={shipTo} onChange={setShipTo} disabled={disabled} />
        {!disabled && (
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save address
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

type LineDraft = { skuId: string; qty: number };

function FullEditForm({
  order, initialLines, skus, customFieldDefs, onSave,
}: {
  order: any;
  initialLines: Array<{ sku_id: string; qty_ordered: number }>;
  skus: Array<{ id: string; sku_code: string; name: string | null }>;
  customFieldDefs: CustomFieldDef[];
  onSave: (payload: {
    orderNumber: string; carrier: string | null; serviceLevel: string | null;
    notes: string | null; shipTo: ReturnType<typeof cleanShipTo>; lines: LineDraft[];
    customFields: Record<string, unknown>;
  }) => Promise<void>;
}) {
  const [orderNumber, setOrderNumber] = useState<string>(order.order_number);
  const [carrier, setCarrier] = useState<string>(order.carrier ?? "");
  const [service, setService] = useState<string>(order.service_level ?? "");
  const [notes, setNotes] = useState<string>(order.notes ?? "");
  const [shipTo, setShipTo] = useState<ShipTo>(normalizeShipTo(order.ship_to));
  const [lines, setLines] = useState<LineDraft[]>(
    initialLines.length
      ? initialLines.map((l) => ({ skuId: l.sku_id, qty: l.qty_ordered }))
      : [{ skuId: "", qty: 1 }],
  );
  const [customFields, setCustomFields] = useState<Record<string, unknown>>(
    (order.custom_fields ?? {}) as Record<string, unknown>,
  );
  const [saving, setSaving] = useState(false);

  const updateLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = lines.filter((l) => l.skuId && l.qty > 0);
    if (clean.length === 0) return toast.error("Add at least one line");
    const cfErrors = validateCustomFields(customFieldDefs, customFields);
    const firstErr = Object.values(cfErrors)[0];
    if (firstErr) return toast.error(firstErr);
    setSaving(true);
    try {
      await onSave({
        orderNumber: orderNumber.trim(),
        carrier: carrier.trim() || null,
        serviceLevel: service.trim() || null,
        notes: notes.trim() || null,
        shipTo: cleanShipTo(shipTo),
        lines: clean,
        customFields,
      });
    } catch (err) { toast.error((err as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-3xl">
      <Card className="p-4 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Order number</Label>
            <Input required value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Carrier</Label>
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS / FedEx / DHL" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Service level</Label>
            <Input value={service} onChange={(e) => setService(e.target.value)} placeholder="ground / express" />
          </div>
        </div>
      </Card>

      <Card className="p-4 space-y-3">
        <h3 className="font-medium">Ship To</h3>
        <ShipToFields value={shipTo} onChange={setShipTo} />
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Lines</h3>
          <Button type="button" size="sm" variant="outline"
            onClick={() => setLines((p) => [...p, { skuId: "", qty: 1 }])}>
            <Plus className="h-4 w-4 mr-1" />Add line
          </Button>
        </div>
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_40px] gap-2 items-end">
            <div className="space-y-1">
              {i === 0 && <Label className="text-xs">SKU</Label>}
              <Select value={l.skuId} onValueChange={(v) => updateLine(i, { skuId: v })}>
                <SelectTrigger><SelectValue placeholder="Pick SKU" /></SelectTrigger>
                <SelectContent>
                  {skus.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name ?? ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              {i === 0 && <Label className="text-xs">Qty</Label>}
              <Input type="number" min={1} max={10000} value={l.qty}
                onChange={(e) => updateLine(i, { qty: parseInt(e.target.value) || 1 })} />
            </div>
            <Button type="button" size="icon" variant="ghost"
              disabled={lines.length === 1}
              onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </Card>

      <Card className="p-4 space-y-2">
        <Label>Notes</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Card>

      {customFieldDefs.length > 0 && (
        <Card className="p-4 space-y-3">
          <h3 className="font-medium">Custom Fields</h3>
          <CustomFieldsForm defs={customFieldDefs} values={customFields} onChange={setCustomFields} />
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save changes
        </Button>
      </div>
    </form>
  );
}

function ReadOnlySummary({
  order, lines, skus, hideShipTo, hideLines,
}: {
  order: any;
  lines: Array<{ sku_id: string; qty_ordered: number }>;
  skus: Array<{ id: string; sku_code: string; name: string | null }>;
  hideShipTo: boolean; hideLines: boolean;
}) {
  const skuMap = useMemo(() => new Map(skus.map((s) => [s.id, s])), [skus]);
  if (hideShipTo && hideLines) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {!hideShipTo && (
        <Card className="p-4 space-y-1 text-sm">
          <h3 className="font-medium mb-2">Ship To</h3>
          {Object.entries(normalizeShipTo(order.ship_to))
            .filter(([, v]) => v)
            .map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k}:</span> {v}</div>)}
        </Card>
      )}
      {!hideLines && (
        <Card className="p-4 space-y-1 text-sm">
          <h3 className="font-medium mb-2">Lines</h3>
          {lines.map((l) => {
            const s = skuMap.get(l.sku_id);
            return (
              <div key={l.sku_id} className="flex justify-between">
                <span className="font-mono text-xs">{s?.sku_code ?? l.sku_id.slice(0, 8)}</span>
                <span>×{l.qty_ordered}</span>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}

/* ---------------- RMA + Return Label dialog ---------------- */

function RmaDialog({
  open, onOpenChange, orderId, orderNumber, lines, skus, onDone,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  orderId: string; orderNumber: string;
  lines: Array<{ sku_id: string; qty_ordered: number }>;
  skus: Array<{ id: string; sku_code: string; name: string | null }>;
  onDone: () => void;
}) {
  const rmaFn = useServerFn(createRmaFromOrder);
  const shopFn = useServerFn(shopReturnRates);
  const buyFn = useServerFn(purchaseReturnLabel);

  const skuMap = useMemo(() => new Map(skus.map((s) => [s.id, s])), [skus]);
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState<Record<string, number>>(() =>
    Object.fromEntries(lines.map((l) => [l.sku_id, l.qty_ordered])),
  );
  const [rmaNumber, setRmaNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createdRma, setCreatedRma] = useState<{ id: string; rma_number: string } | null>(null);

  // Return label state
  const [carrier, setCarrier] = useState<"ups" | "usps" | "fedex">("ups");
  const [weight, setWeight] = useState(16);
  const [length, setLength] = useState(10);
  const [width, setWidth] = useState(8);
  const [height, setHeight] = useState(4);
  const [rates, setRates] = useState<Array<{
    carrier: string; service_code: string; service_name: string;
    rate_cents: number; est_transit_days: number | null;
  }>>([]);
  const [picking, setPicking] = useState<string | null>(null);
  const [label, setLabel] = useState<{
    tracking_number: string | null; label_url: string | null; carrier: string;
  } | null>(null);

  const reset = () => {
    setReason(""); setRmaNumber(""); setCreatedRma(null);
    setRates([]); setLabel(null); setPicking(null);
    setSelected(Object.fromEntries(lines.map((l) => [l.sku_id, l.qty_ordered])));
  };

  const submitRma = async () => {
    const chosen = Object.entries(selected)
      .filter(([, q]) => q > 0)
      .map(([skuId, qty]) => ({ skuId, qty }));
    if (chosen.length === 0) return toast.error("Select at least one line");
    setSubmitting(true);
    try {
      const res = await runServerFn(() => rmaFn({
        data: {
          orderId, reason: reason.trim() || null,
          rmaNumber: rmaNumber.trim() || undefined,
          lines: chosen,
        },
      }), "Creating RMA");
      setCreatedRma(res);
      toast.success(`RMA ${res.rma_number} created`);
      onDone();
    } catch (e) { toast.error((e as Error).message); }
    finally { setSubmitting(false); }
  };

  const shopRates = async () => {
    if (!createdRma) return;
    try {
      const res = await runServerFn(() => shopFn({
        data: {
          rmaId: createdRma.id,
          carriers: [carrier],
          pkg: { weight_oz: weight, length_in: length, width_in: width, height_in: height },
        },
      }), "Shopping rates");
      setRates(res.rates);
      if (res.rates.length === 0) toast.message("No rates returned for that carrier");
    } catch (e) { toast.error((e as Error).message); }
  };

  const purchase = async (svc: { service_code: string; service_name: string }) => {
    if (!createdRma) return;
    setPicking(svc.service_code);
    try {
      const res = await runServerFn(() => buyFn({
        data: {
          rmaId: createdRma.id, carrier, serviceCode: svc.service_code,
          serviceName: svc.service_name,
          pkg: { weight_oz: weight, length_in: length, width_in: width, height_in: height },
        },
      }), "Buying return label");
      setLabel({ tracking_number: res.tracking_number, label_url: res.label_url, carrier: res.carrier });
      toast.success("Return label purchased");
    } catch (e) { toast.error((e as Error).message); }
    finally { setPicking(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create RMA for order {orderNumber}</DialogTitle>
          <DialogDescription>
            Select returning lines, then optionally generate a return label.
          </DialogDescription>
        </DialogHeader>

        {!createdRma ? (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">RMA number (optional)</Label>
                <Input value={rmaNumber} onChange={(e) => setRmaNumber(e.target.value)} placeholder="auto" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reason</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Damaged, wrong item, …" />
              </div>
            </div>
            <div className="border rounded-md divide-y">
              {lines.map((l) => {
                const s = skuMap.get(l.sku_id);
                return (
                  <div key={l.sku_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs">{s?.sku_code ?? l.sku_id.slice(0, 8)}</div>
                      <div className="text-muted-foreground text-xs truncate">{s?.name ?? ""}</div>
                    </div>
                    <div className="text-muted-foreground text-xs">ordered ×{l.qty_ordered}</div>
                    <Input type="number" min={0} max={l.qty_ordered} className="w-20"
                      value={selected[l.sku_id] ?? 0}
                      onChange={(e) => setSelected((p) => ({
                        ...p, [l.sku_id]: Math.max(0, Math.min(l.qty_ordered, parseInt(e.target.value) || 0)),
                      }))} />
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={submitRma} disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create RMA
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-100">
              RMA <span className="font-mono">{createdRma.rma_number}</span> created.
            </div>

            {!label ? (
              <>
                <h4 className="text-sm font-medium">Generate return label</h4>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="text-xs">Carrier</Label>
                    <Select value={carrier} onValueChange={(v) => setCarrier(v as any)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ups">UPS</SelectItem>
                        <SelectItem value="usps">USPS</SelectItem>
                        <SelectItem value="fedex">FedEx</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Wt (oz)</Label>
                    <Input type="number" min={0.1} value={weight} onChange={(e) => setWeight(parseFloat(e.target.value) || 1)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">L (in)</Label>
                    <Input type="number" min={1} value={length} onChange={(e) => setLength(parseFloat(e.target.value) || 1)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">W (in)</Label>
                    <Input type="number" min={1} value={width} onChange={(e) => setWidth(parseFloat(e.target.value) || 1)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">H (in)</Label>
                    <Input type="number" min={1} value={height} onChange={(e) => setHeight(parseFloat(e.target.value) || 1)} />
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={shopRates}>Get rates</Button>
                {rates.length > 0 && (
                  <div className="border rounded-md divide-y">
                    {rates.map((r) => (
                      <div key={r.service_code} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div>
                          <div className="font-medium">{r.service_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.carrier.toUpperCase()} · {r.est_transit_days ?? "—"} day(s)
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm">${(r.rate_cents / 100).toFixed(2)}</span>
                          <Button size="sm" disabled={picking === r.service_code}
                            onClick={() => purchase({ service_code: r.service_code, service_name: r.service_name })}>
                            {picking === r.service_code && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}Buy label
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
                </DialogFooter>
              </>
            ) : (
              <div className="space-y-3">
                <div className="rounded-md border p-3 text-sm space-y-1">
                  <div><span className="text-muted-foreground">Carrier:</span> {label.carrier.toUpperCase()}</div>
                  <div className="font-mono"><span className="text-muted-foreground font-sans">Tracking:</span> {label.tracking_number}</div>
                  {label.label_url && (
                    <a href={label.label_url} target="_blank" rel="noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1">
                      Download label PDF <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}